import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/lib/data";
import { BloodDrip } from "@/components/BloodDrip";

export default function Gallery() {
  const [active, setActive] = useState(null);
  const activeIndex = GALLERY.findIndex((g) => g.src === active?.src);
  const cycle = (dir) => {
    if (activeIndex < 0) return;
    setActive(GALLERY[(activeIndex + dir + GALLERY.length) % GALLERY.length]);
  };

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") cycle(1);
      if (e.key === "ArrowLeft") cycle(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section id="gallery" className="relative py-28 sm:py-36 bg-pulp/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-marker text-paper text-base sm:text-lg mb-3 rotate-1 inline-block"
        >
          gaze upon her — she gazes back
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-paper mb-16"
        >
          ART <span className="text-crimson">SHOWCASE</span>
          <BloodDrip />
        </motion.h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {GALLERY.map((g, i) => (
            <motion.button
              key={g.src}
              data-testid={`art-gallery-item-${i}`}
              onClick={() => setActive(g)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className="group relative block w-full border-2 border-border overflow-hidden transition-colors duration-200 hover:border-yell aspect-[2/3]"
            >
              <img src={g.src} alt={g.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-void/0 group-hover:bg-void/45 transition-colors duration-300 flex items-center justify-center">
                <ZoomIn className="text-yell opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
              </div>
              <span className="absolute bottom-0 inset-x-0 bg-void/85 font-courier text-[10px] uppercase tracking-widest text-paper/80 px-3 py-2 text-left translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                {g.title} — {g.artist}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="art-lightbox-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-50 bg-void/92 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
          >
            <button
              data-testid="art-lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                cycle(-1);
              }}
              className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 bg-crimson text-white border-2 border-black p-2 ink-shadow-sm hover:bg-crimsonhot transition-colors duration-150"
              aria-label="Previous artwork"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="art-lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                cycle(1);
              }}
              className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 bg-crimson text-white border-2 border-black p-2 ink-shadow-sm hover:bg-crimsonhot transition-colors duration-150"
              aria-label="Next artwork"
            >
              <ChevronRight size={22} />
            </button>
            <motion.figure
              initial={{ scale: 0.9, rotate: -1 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-full"
            >
              <img src={active.src} alt={active.title} className="max-h-[80vh] w-auto border-4 border-crimson comic-shadow" />
              <figcaption className="absolute -bottom-4 left-4 bg-blooddeep text-white font-marker text-sm px-4 py-1.5 -rotate-2 ink-shadow-sm">
                {active.title} — {active.artist}
              </figcaption>
              <span data-testid="art-lightbox-counter" className="absolute -bottom-4 right-4 bg-void/85 font-courier text-xs text-paper/60 px-3 py-1.5 border border-paper/20">
                {activeIndex + 1} / {GALLERY.length}
              </span>
              <button
                data-testid="art-lightbox-close"
                onClick={() => setActive(null)}
                className="absolute -top-4 -right-4 bg-crimson text-white border-2 border-black p-2 ink-shadow-sm hover:bg-crimsonhot transition-colors duration-150"
                aria-label="Close lightbox"
              >
                <X size={20} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
